import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAccessGate } from '@/contexts/use-admin-access-gate';
import { useAbility, useAbilityLoading } from '@/components/authnz/AbilityContext';
import { useToast } from '@/hooks/use-toast';
import PageLayout from '@/components/shared/PageLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ProductManagement from './ProductManagement';
import SubscriptionManagement from './SubscriptionManagement';

export default function SubscriptionsPage() {
  const {
    isReady,
    isAuthorizedAdmin,
    hasAdminAccess,
  } = useAdminAccessGate();
  const ability = useAbility();
  const abilityLoading = useAbilityLoading();
  const navigate = useNavigate();
  const { toast } = useToast();
  const canCreateSubscription = ability.can('create', 'Subscription');
  const canAccessPage = hasAdminAccess || canCreateSubscription;
  const showProductsTab = isAuthorizedAdmin;

  useEffect(() => {
    if (!isReady || abilityLoading) {
      return;
    }

    if (!canAccessPage) {
      toast({
        title: 'Access denied',
        description: 'You do not have permission to manage subscriptions.',
        variant: 'destructive',
      });
      navigate('/settings');
    }
  }, [abilityLoading, canAccessPage, isReady, navigate, toast]);

  if (!isReady || abilityLoading || !canAccessPage) {
    return null;
  }

  return (
    <PageLayout
      title="Subscriptions & Products"
      subtitle="Manage products and user subscriptions"
    >
      {showProductsTab ? (
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          </TabsList>
          <TabsContent value="products" className="mt-0">
            <ProductManagement />
          </TabsContent>
          <TabsContent value="subscriptions" className="mt-0">
            <SubscriptionManagement />
          </TabsContent>
        </Tabs>
      ) : (
        <SubscriptionManagement />
      )}
    </PageLayout>
  );
}
